import React from "react";
import Modal, { ModalProps } from "components/Modal/Modal";
import {
  ShipSuiteModalDataQuery,
  SuiteModalShipVersionFragment
} from "types/graphql-types";
import SuiteModalContent from "./SuiteModalContent";
import { snakeCase } from "src/utils/strings";
import useFetchModalData from "src/utils/useFetchModalData";
import {
  isSanityImagePresent,
  ModalBody,
  Price
} from "@silversea-ssc/ui-components-react";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { getShipVersionFromKey } from "src/utils/ship";
import { isPresent } from "src/utils/checks";
import { SlideshowItem } from "components/Slideshow/Slideshow";
import { useLingui } from "@lingui/react/macro";

export interface ShipSuiteModalProps {
  modal: ModalProps["modal"];
  suiteCode?: string;
  shipPath: string;
  prices?: Price[];
  raqCTAUrl?: string;
  ecommerceStatus?: ECommerceStatus;
  shipVersionKey: string;
}

const deckPlanImagesForDeckNumbers = (
  deckPlans: SuiteModalShipVersionFragment["decks"],
  decks: number[],
  deckLabel: string
): SlideshowItem[] | undefined => {
  return deckPlans
    ?.filter((x) => x?.deckNumber && decks.includes(x.deckNumber))
    .map((d) => {
      if (!isPresent(d) || !isSanityImagePresent(d.deckImage)) return undefined;

      return {
        ...d.deckImage,
        asset: {
          ...d.deckImage.asset,
          metadata: {
            preview: undefined
          }
        },
        __typename: "SanityImageWithCaption" as const,
        caption: { localized: `${deckLabel} ${d?.deckNumber}` }
      };
    })
    .filter(isPresent);
};

const ShipSuiteModal: React.FC<
  React.PropsWithChildren<ShipSuiteModalProps>
> = ({
  modal,
  shipPath,
  suiteCode,
  prices,
  raqCTAUrl,
  ecommerceStatus,
  shipVersionKey
}) => {
  const { i18n, t } = useLingui();
  const { data } = useFetchModalData<ShipSuiteModalDataQuery>(
    `/suites${shipPath}`
  );

  const versions = data?.ship?.versions;

  if (!versions) return <></>;

  const version = getShipVersionFromKey(versions, shipVersionKey);
  const content = version?.suites?.find((s) => {
    const codes = s?.configurations?.map((x) => x?.code) || []; // TODO: cleanup
    return codes.includes(suiteCode);
  });

  if (!version || !content) return <></>;

  const deckLabel = i18n._(t`deck`);
  const allDeckPlans = version?.decks;
  const validDeckNumbers = content.deck?.filter(isPresent);
  const deckPlansImages = validDeckNumbers
    ? deckPlanImagesForDeckNumbers(allDeckPlans, validDeckNumbers, deckLabel)
    : undefined;

  return (
    <Modal
      title={content?.card?.title?.localized}
      modal={modal}
      size="big"
      dataContentName={`modal_${snakeCase(content?.card?.title?.en || "")}`}
    >
      <ModalBody variant="wide">
        {content != null && (
          <SuiteModalContent
            suite={content}
            prices={prices}
            raqCTAUrl={raqCTAUrl}
            ecommerceStatus={ecommerceStatus}
            deckPlanImages={deckPlansImages}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default ShipSuiteModal;
